
import aliasb03645cf6abd4d56a73b13687765a5d8 from '@/locale/i18nUtils.js';
/**
 * @Description: 商品详情操作日志解析与格式化
 * @date 2026/6/22
 */
import api from '@/api/';
import {getProductDetailsLogCompareItem} from '@/utils/logTemplate';
import {getAllCountrysData, getDataSetBatchList, getEnumList, isChineseLanguage, isJSON} from '@/utils/common';

/** 商品详情日志字段编码正则（100000 ~ 100999，兼容后续新增编码） */
const FIELD_KEY_REG = /^100\d{3}$/;

/**
 * 判断 key 是否为商品详情日志字段编码
 * @param {string} key
 * @returns {boolean}
 */
const isProductFieldKey = (key) => FIELD_KEY_REG.test(key);

/**
 * 判断单条变更项是否符合日志结构
 * @param {*} item
 * @returns {boolean}
 */
const isLogChangeItem = (item) => {
  if (!item || typeof item !== 'object') return false;
  if (item.value !== undefined && item.value !== null) return true;
  if (item.prefix !== undefined) return true;
  return item.oldValue !== undefined || item.newValue !== undefined;
};

/**
 * 通用收集 prefix / value 中的 search 类型 ID（用于未注册的新增字段编码）
 * @param {Array} prefix
 * @param {Object} value
 * @param {Object} collectors
 */
const collectGenericSearchIds = (prefix, value, collectors) => {
  const {
    warehouseIds,
    attrTemplateIds,
    attrDictIds,
    sizePartsIds,
    productGoodsIds,
    countryCodes
  } = collectors;

  prefix.forEach((p, index) => {
    if (!p || p.type !== 'search') return;
    if (index === 0) {
      addToSet(productGoodsIds, p.value);
      addToSet(attrTemplateIds, p.value);
    } else if (index === 1) {
      addToSet(warehouseIds, p.value);
    } else {
      addToSet(sizePartsIds, p.value);
    }
  });

  prefix.forEach((p) => {
    if (p && p.type === 'default' && p.value) {
      addToSet(countryCodes, p.value);
    }
  });

  if (value.type === 'search') {
    addToSet(warehouseIds, value.oldValue);
    addToSet(warehouseIds, value.newValue);
    addToSet(attrDictIds, value.oldValue);
    addToSet(attrDictIds, value.newValue);
  }
};

/** 阶梯价格类型文案映射 */
const SCHEME_TYPE_LABEL = {0: aliasb03645cf6abd4d56a73b13687765a5d8.t('key1000980'), 1: aliasb03645cf6abd4d56a73b13687765a5d8.t('key1000981')};

/** 商品是否支持定制文案映射（与 productDetails 单选一致） */
const CUSTOMIZATION_LABEL = {'0': aliasb03645cf6abd4d56a73b13687765a5d8.t('key1000393'), '1': aliasb03645cf6abd4d56a73b13687765a5d8.t('key1000392')};

/** 发货时效文案映射（与 product_mixin.deliveryIntervalList 一致） */
const DELIVERY_INTERVAL_LABEL = {
  '1': aliasb03645cf6abd4d56a73b13687765a5d8.t('key1000165'),
  '2': aliasb03645cf6abd4d56a73b13687765a5d8.t('key1000166'),
  '3': aliasb03645cf6abd4d56a73b13687765a5d8.t('key1000167'),
  '4': aliasb03645cf6abd4d56a73b13687765a5d8.t('key1000168'),
  '5': aliasb03645cf6abd4d56a73b13687765a5d8.t('key1000169'),
  '6': aliasb03645cf6abd4d56a73b13687765a5d8.t('key1000170'),
  '7': aliasb03645cf6abd4d56a73b13687765a5d8.t('key1000171')
};

/** 商品服务文案映射（与 common_mixin.productServiceList 一致） */
const PRODUCT_SERVICE_LABEL = {'1': aliasb03645cf6abd4d56a73b13687765a5d8.t('key1000137')};

/**
 * 获取阶梯价格类型展示文案
 * @param {number|string} schemeType
 * @returns {string|undefined}
 */
const getSchemeTypeLabel = (schemeType) => {
  if (schemeType === null || schemeType === undefined || schemeType === '') return undefined;
  return SCHEME_TYPE_LABEL[schemeType] || SCHEME_TYPE_LABEL[Number(schemeType)];
};

/**
 * 取第一个非 null/undefined 的值
 * @param {...*} values
 * @returns {*}
 */
const pickDefined = (...values) => {
  for (let i = 0; i < values.length; i++) {
    const val = values[i];
    if (val !== undefined && val !== null) {
      return val;
    }
  }
  return undefined;
};

/**
 * 空值时使用默认值（兼容 ES2018，替代 ??）
 * @param {*} val
 * @param {*} fallback
 * @returns {*}
 */
const defaultVal = (val, fallback) => (val === null || val === undefined ? fallback : val);

/**
 * 按当前语种取多语种名称
 * @param {Object} item
 * @param {string} zhKey
 * @param {string} enKey
 * @returns {string}
 */
const getLocalizedName = (item, zhKey, enKey) => {
  if (!item) return '';
  if (isChineseLanguage()) {
    return item[zhKey] || item[enKey] || item.name || '';
  }
  return item[enKey] || item[zhKey] || item.name || '';
};

/**
 * 按当前语种取尺码部位名称
 * @param {Object} item
 * @returns {string}
 */
const getSizePartName = (item) => {
  if (!item) return '';
  if (isChineseLanguage()) {
    return item.cnName || item.enName || item.name || '';
  }
  return item.usName || item.enName || item.name || item.cnName || '';
};

/**
 * 从映射表解析 ID，找不到时降级返回原始 ID
 * @param {string|number} id
 * @param {Object} map
 * @returns {*}
 */
const resolveMapValue = (id, map) => {
  if (id === null || id === undefined || id === '') return id;
  const mapped = pickDefined(map[id], map[Number(id)], map[String(id)]);
  return mapped !== undefined && mapped !== null ? mapped : id;
};

/**
 * 将单值/数组/逗号分隔字符串解析为映射表展示文案
 * @param {*} value
 * @param {Object} map
 * @param {string} [separator='、']
 * @returns {string}
 */
const resolveLabelArray = (value, map, separator = '、') => {
  if (value === null || value === undefined || value === '') return '';
  let list = value;
  if (Array.isArray(value)) {
    list = value;
  } else if (typeof value === 'string' && value.includes(',')) {
    list = value.split(',').map((item) => item.trim()).filter(Boolean);
  } else {
    list = [value];
  }
  return list
    .map((item) => resolveMapValue(item, map))
    .filter((item) => item !== null && item !== undefined && item !== '')
    .join(separator);
};

/**
 * 按固定文案映射表解析字段值
 * @param {*} value
 * @param {Object} labelMap
 * @returns {string}
 */
const resolveFixedLabelValue = (value, labelMap) => {
  if (value === null || value === undefined || value === '') return '';
  const mapped = pickDefined(labelMap[value], labelMap[Number(value)], labelMap[String(value)]);
  return mapped !== undefined && mapped !== null ? mapped : String(value);
};

/**
 * 向 Set 中追加值，支持数组递归展开
 * @param {Set} set
 * @param {*} value
 */
const addToSet = (set, value) => {
  if (value === null || value === undefined || value === '') return;
  if (Array.isArray(value)) {
    value.forEach((item) => addToSet(set, item));
    return;
  }
  set.add(value);
};

/**
 * 获取待渲染的字段 key 列表（按编码升序）
 * @param {Object} parsed
 * @param {string} [operationType]
 * @returns {string[]}
 */
const getProductFieldKeys = (parsed, operationType) => {
  return Object.keys(parsed)
    .filter((key) => {
      const items = parsed[key];
      if (!Array.isArray(items) || !items.length) return false;
      if (isProductFieldKey(key)) return true;
      if (operationType === 'UPDATE_PRODUCT_INFO') {
        return items.some(isLogChangeItem);
      }
      return false;
    })
    .sort((a, b) => {
      const numA = Number(a);
      const numB = Number(b);
      if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
      return String(a).localeCompare(String(b));
    });
};

/**
 * 判断解析结果是否为商品详情日志结构
 * @param {Object} parsed
 * @param {string} [operationType]
 * @returns {boolean}
 */
const isProductDetailsLogContent = (parsed, operationType) => {
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return false;
  if (getProductFieldKeys(parsed, operationType).length > 0) return true;
  if (operationType === 'UPDATE_PRODUCT_INFO') {
    return Object.keys(parsed).some((key) => isProductFieldKey(key));
  }
  return false;
};

/**
 * 将分层价格方案数组格式化为可读字符串
 * @param {Array} schemeList
 * @returns {string}
 */
export const formatPriceSchemeList = (schemeList) => {
  if (!Array.isArray(schemeList) || !schemeList.length) return '-';
  return schemeList.map((item) => {
    const end = item.endSalesVolume === null || item.endSalesVolume === undefined ? aliasb03645cf6abd4d56a73b13687765a5d8.t('key1000836') : item.endSalesVolume;
    const range = `${item.startSalesVolume}${aliasb03645cf6abd4d56a73b13687765a5d8.t('key1000828')}${end}`;
    if (item.schemeType === 0 || item.schemeType === '0') {
      return `${range}${aliasb03645cf6abd4d56a73b13687765a5d8.t('key1002147')}${item.schemeValue}`;
    }
    if (item.schemeType === 1 || item.schemeType === '1') {
      return `${range}${aliasb03645cf6abd4d56a73b13687765a5d8.t('key1002148')}${item.schemeValue}`;
    }
    return `${range}，${item.schemeValue}`;
  }).join('；');
};

/**
 * 将属性字典 ID 数组解析为名称并用顿号拼接（与 productDetails 属性展示一致）
 * @param {Array|string} value
 * @param {Object} dictMap
 * @param {string} [separator='、']
 * @returns {string}
 */
const resolveDictArray = (value, dictMap, separator = '、') => {
  if (Array.isArray(value)) {
    return value
      .map((id) => resolveMapValue(id, dictMap))
      .filter((item) => item !== null && item !== undefined && item !== '')
      .join(separator);
  }
  return resolveMapValue(value, dictMap);
};

/**
 * 判断变更项是否为带单位的自定义属性（含 oldUnit/newUnit 字段）
 * @param {Object} value
 * @returns {boolean}
 */
const hasAttributeUnitField = (value) => {
  if (!value || typeof value !== 'object') return false;
  return 'oldUnit' in value || 'newUnit' in value;
};

/**
 * 将自定义属性数值与单位 ID 拼接为「数值 单位名」展示文案
 * @param {*} rawValue
 * @param {string|number} unitId
 * @param {Object} dictMap
 * @returns {string}
 */
const formatAttributeValueWithUnit = (rawValue, unitId, dictMap) => {
  let valueText = '';
  if (rawValue !== null && rawValue !== undefined && rawValue !== '') {
    if (Array.isArray(rawValue)) {
      valueText = rawValue
        .filter((item) => item !== null && item !== undefined && item !== '')
        .join('、');
    } else {
      valueText = String(rawValue);
    }
  }
  const unitText = unitId ? resolveMapValue(unitId, dictMap) : '';
  if (!valueText && !unitText) return '';
  if (!valueText) return String(unitText);
  if (!unitText) return valueText;
  return `${valueText} ${unitText}`;
};

/**
 * 归一化变更 value：兼容对象与数组两种结构
 * 100009/100010 等字段 value 可能为 [{ oldValue, newValue, type }]
 * @param {*} value
 * @returns {*}
 */
const normalizeChangeValue = (value) => {
  if (!Array.isArray(value)) return value;
  const first = value[0];
  if (first && typeof first === 'object' && !Array.isArray(first)) {
    return first;
  }
  return value;
};

/**
 * 获取单条变更项的 value 对象
 * @param {Object} item
 * @returns {Object}
 */
const getItemValue = (item) => {
  if (!item) return {};
  if (item.value !== null && item.value !== undefined) {
    return normalizeChangeValue(item.value);
  }
  return item;
};

/**
 * 获取单条变更项的 prefix 数组
 * @param {Object} item
 * @returns {Array}
 */
const getItemPrefix = (item) => (item && Array.isArray(item.prefix) ? item.prefix : []);

/**
 * 扫描 operateContent，按类型收集待批量查询的 ID
 * @param {Object} parsed - JSON.parse 后的 operateContent
 * @returns {Object}
 */
const collectIds = (parsed) => {
  const warehouseIds = new Set();
  const attrTemplateIds = new Set();
  const attrDictIds = new Set();
  const sizePartsIds = new Set();
  const productGoodsIds = new Set();
  const countryCodes = new Set();

  const collectors = {
    warehouseIds,
    attrTemplateIds,
    attrDictIds,
    sizePartsIds,
    productGoodsIds,
    countryCodes
  };

  Object.entries(parsed).forEach(([fieldKey, items]) => {
    if (!isProductFieldKey(fieldKey) || !Array.isArray(items)) return;

    items.forEach((item) => {
      const prefix = getItemPrefix(item);
      const value = getItemValue(item);
      const prefix0 = prefix[0];
      const prefix1 = prefix[1];

      switch (fieldKey) {
        case '100003':
          if (value.type === 'search') {
            addToSet(warehouseIds, value.oldValue);
            addToSet(warehouseIds, value.newValue);
          }
          break;
        case '100009':
        case '100010':
          prefix.forEach((p) => {
            if (p.type === 'search') addToSet(attrTemplateIds, p.value);
          });
          if (hasAttributeUnitField(value)) {
            addToSet(attrDictIds, value.oldUnit);
            addToSet(attrDictIds, value.newUnit);
          } else if (value.type === 'search') {
            addToSet(attrDictIds, value.oldValue);
            addToSet(attrDictIds, value.newValue);
          }
          break;
        case '100011':
        case '100012':
        case '100013':
          if (prefix0 && prefix0.type === 'search') addToSet(productGoodsIds, prefix0.value);
          break;
        case '100014':
          if (prefix0 && prefix0.type === 'search') addToSet(productGoodsIds, prefix0.value);
          if (prefix1 && prefix1.type === 'default') addToSet(countryCodes, prefix1.value);
          break;
        case '100015':
        case '100016':
          if (prefix0 && prefix0.type === 'search') addToSet(productGoodsIds, prefix0.value);
          if (prefix1 && prefix1.type === 'search') addToSet(warehouseIds, prefix1.value);
          break;
        case '100017':
          prefix.forEach((p) => {
            if (!p || !p.value) return;
            if (p.type === 'search') addToSet(attrDictIds, p.value);
            if (p.type === 'default') addToSet(sizePartsIds, p.value);
          });
          break;
        default:
          collectGenericSearchIds(prefix, value, collectors);
          break;
      }
    });
  });

  return {
    warehouseIds: [...warehouseIds],
    attrTemplateIds: [...attrTemplateIds],
    attrDictIds: [...attrDictIds],
    sizePartsIds: [...sizePartsIds],
    productGoodsIds: [...productGoodsIds],
    countryCodes: [...countryCodes]
  };
};

/**
 * 批量查询仓库 ID → 名称映射
 * @param {Object} axios
 * @param {Array} warehouseIds
 * @returns {Promise<Object>}
 */
const fetchWarehouseMap = async (axios, warehouseIds) => {
  const map = {};
  if (!warehouseIds.length) return map;
  try {
    const {data: res} = await axios.post(api.post_ymsWarehouse_queryList, {warehouseIds});
    if (res && res.code === 0) {
      (res.datas || []).forEach((item) => {
        map[item.ymsWarehouseId] = item.name || item.aliasWarehouseName || item.ymsWarehouseId;
      });
    }
  } catch (e) {
    console.error('fetchWarehouseMap error:', e);
  }
  return map;
};

/**
 * 批量查询属性模板及嵌套字典 ID → 名称映射
 * @param {Object} axios
 * @param {Array} templateIds
 * @returns {Promise<{templateMap: Object, dictMap: Object}>}
 */
const fetchAttrMaps = async (axios, templateIds) => {
  const templateMap = {};
  const dictMap = {};
  if (!templateIds.length) return {templateMap, dictMap};
  try {
    const {data: res} = await axios.post(api.post_ymsProductAttributeTemplate_queryList, {
      ymsProductAttributeTemplateIds: templateIds
    });
    if (res && res.code === 0) {
      (res.datas || []).forEach((tpl) => {
        templateMap[tpl.ymsProductAttributeTemplateId] = getLocalizedName(tpl, 'cnName', 'usName');
        (tpl.productAttributeDictionaryBos || []).forEach((dict) => {
          dictMap[dict.ymsProductAttributeDictionaryId] = getLocalizedName(dict, 'cnValue', 'usValue');
        });
      });
    }
  } catch (e) {
    console.error('fetchAttrMaps error:', e);
  }
  return {templateMap, dictMap};
};

/**
 * 属性字典补查：queryList 未覆盖的 ID 单独查询
 * @param {Object} axios
 * @param {Array} dictIds
 * @param {Object} dictMap - 会被原地写入
 */
const fetchAttrDictFallback = async (axios, dictIds, dictMap) => {
  const missingIds = dictIds.filter((id) => !dictMap[id]);
  if (!missingIds.length) return;
  try {
    const {data: res} = await axios.post(api.post_ymsProductAttributeDictionary_query, missingIds);
    if (res && res.code === 0) {
      (res.datas || []).forEach((dict) => {
        dictMap[dict.ymsProductAttributeDictionaryId] = getLocalizedName(dict, 'cnValue', 'usValue');
      });
    }
  } catch (e) {
    console.error('fetchAttrDictFallback error:', e);
  }
};

/**
 * 批量查询尺码部位 ID → 名称映射
 * @param {Object} axios
 * @param {Array} sizePartsIds
 * @returns {Promise<Object>}
 */
const fetchSizePartsMap = async (axios, sizePartsIds) => {
  const map = {};
  if (!sizePartsIds.length) return map;
  try {
    const {data: res} = await axios.post(api.post_ymsProductSizeTemplate_queryYmsProductSizePartsIds, sizePartsIds);
    if (res && res.code === 0) {
      (res.datas || []).forEach((item) => {
        map[item.ymsProductSizePartsId] = getSizePartName(item);
      });
    }
  } catch (e) {
    console.error('fetchSizePartsMap error:', e);
  }
  return map;
};

/**
 * 批量查询货品 ID → ymsSku 映射
 * @param {Object} axios
 * @param {Array} productGoodsIds
 * @returns {Promise<Object>}
 */
const fetchProductGoodsMap = async (axios, productGoodsIds) => {
  const map = {};
  if (!productGoodsIds.length) return map;
  try {
    const {data: res} = await axios.post(api.post_ymsProductInfo_queryByYmsProductGoodsIds, productGoodsIds);
    if (res && res.code === 0) {
      (res.datas || []).forEach((product) => {
        (product.productGoodsList || []).forEach((goods) => {
          const id = goods.ymsProductGoodsId || goods.productGoodsId;
          if (id) {
            map[id] = goods.ymsSku || goods.sku || id;
          }
        });
      });
    }
  } catch (e) {
    console.error('fetchProductGoodsMap error:', e);
  }
  return map;
};

/**
 * 批量查询图片类型枚举 value → 名称映射
 * @returns {Promise<Object>}
 */
const fetchImageTypeMap = async () => {
  const map = {};
  try {
    const enumData = await getDataSetBatchList([
      'ProductInfoImageTypeSupplierEnum',
      'YmsProductInfoImageTypeCoreEnum'
    ]);
    ['ProductInfoImageTypeSupplierEnum', 'YmsProductInfoImageTypeCoreEnum'].forEach((enumKey) => {
      getEnumList(enumData, enumKey, 'number').forEach((item) => {
        map[item.value] = item.name || item.title || item.label;
      });
    });
  } catch (e) {
    console.error('fetchImageTypeMap error:', e);
  }
  return map;
};

/**
 * 批量查询国家 twoCode → 名称映射
 * @returns {Promise<Object>}
 */
const fetchCountryMap = async () => {
  const map = {};
  try {
    const list = await getAllCountrysData('cn', true);
    list.forEach((item) => {
      map[item.twoCode] = item.name || item.cnName || item.twoCode;
    });
  } catch (e) {
    console.error('fetchCountryMap error:', e);
  }
  return map;
};

/**
 * 将轮播图日志值归一化为图片路径数组
 * @param {*} value
 * @returns {string[]}
 */
const normalizeImagePathList = (value) => {
  if (value === null || value === undefined || value === '') return [];
  if (Array.isArray(value)) {
    return value.filter((item) => item !== null && item !== undefined && item !== '').map(String);
  }
  if (typeof value === 'string') {
    return value.split(/[,、]/).map((item) => item.trim()).filter(Boolean);
  }
  return [String(value)];
};

/**
 * 按字段编码将单条变更项组装为 logTemplate 入参
 * @param {string} fieldKey
 * @param {Object} item
 * @param {Object} maps - 各类 ID 映射表集合
 * @returns {Object}
 */
const buildTemplateVal = (fieldKey, item, maps) => {
  const prefix = getItemPrefix(item);
  const value = getItemValue(item);
  const prefix0 = prefix[0];
  const prefix1 = prefix[1];
  const {
    warehouseMap,
    templateMap,
    dictMap,
    sizePartsMap,
    productGoodsMap,
    countryMap,
    imageTypeMap
  } = maps;

  switch (fieldKey) {
    case '100000':
      return {
        oldValue: resolveFixedLabelValue(value.oldValue, CUSTOMIZATION_LABEL),
        newValue: resolveFixedLabelValue(value.newValue, CUSTOMIZATION_LABEL)
      };
    case '100004':
      return {
        oldValue: resolveMapValue(value.oldValue, countryMap),
        newValue: resolveMapValue(value.newValue, countryMap)
      };
    case '100005':
      return {
        valueType: 'imageList',
        oldImages: normalizeImagePathList(value.oldValue),
        newImages: normalizeImagePathList(value.newValue)
      };
    case '100006':
      return {
        oldValue: resolveLabelArray(value.oldValue, imageTypeMap),
        newValue: resolveLabelArray(value.newValue, imageTypeMap)
      };
    case '100018':
      return {
        oldValue: resolveFixedLabelValue(value.oldValue, PRODUCT_SERVICE_LABEL),
        newValue: resolveFixedLabelValue(value.newValue, PRODUCT_SERVICE_LABEL)
      };
    case '100019':
      return {
        oldValue: resolveFixedLabelValue(value.oldValue, DELIVERY_INTERVAL_LABEL),
        newValue: resolveFixedLabelValue(value.newValue, DELIVERY_INTERVAL_LABEL)
      };
    case '100003':
      return {
        oldValue: resolveLabelArray(value.oldValue, warehouseMap),
        newValue: resolveLabelArray(value.newValue, warehouseMap)
      };
    case '100009':
    case '100010': {
      const searchPrefix = prefix.find((p) => p.type === 'search');
      const templateId = searchPrefix ? searchPrefix.value : undefined;
      const attrName = resolveMapValue(templateId, templateMap);
      if (hasAttributeUnitField(value)) {
        return {
          attrName,
          oldValue: formatAttributeValueWithUnit(value.oldValue, value.oldUnit, dictMap),
          newValue: formatAttributeValueWithUnit(value.newValue, value.newUnit, dictMap)
        };
      }
      if (value.type === 'search') {
        return {
          attrName,
          oldValue: resolveDictArray(value.oldValue, dictMap),
          newValue: resolveDictArray(value.newValue, dictMap)
        };
      }
      return {
        attrName,
        oldValue: defaultVal(value.oldValue, ''),
        newValue: defaultVal(value.newValue, '')
      };
    }
    case '100011':
    case '100012':
    case '100013': {
      const goodsId = prefix0 ? prefix0.value : undefined;
      const ymsSku = resolveMapValue(goodsId, productGoodsMap) || value.oldValue || goodsId || '';
      return {
        ymsSku,
        oldValue: defaultVal(value.oldValue, ''),
        newValue: defaultVal(value.newValue, '')
      };
    }
    case '100014': {
      const goodsId = prefix0 ? prefix0.value : undefined;
      const countryCode = prefix1 ? prefix1.value : undefined;
      return {
        ymsSku: resolveMapValue(goodsId, productGoodsMap) || goodsId || '',
        countryName: resolveMapValue(countryCode, countryMap),
        oldValue: defaultVal(value.oldValue, ''),
        newValue: defaultVal(value.newValue, '')
      };
    }
    case '100015':
    case '100016': {
      const goodsId = prefix0 ? prefix0.value : undefined;
      const warehouseId = prefix1 ? prefix1.value : undefined;
      return {
        ymsSku: resolveMapValue(goodsId, productGoodsMap) || goodsId || '',
        warehouseName: resolveMapValue(warehouseId, warehouseMap),
        oldValue: defaultVal(value.oldValue, ''),
        newValue: defaultVal(value.newValue, '')
      };
    }
    case '100017': {
      const sizeValueId = prefix0 ? prefix0.value : undefined;
      const partId = prefix1 ? prefix1.value : undefined;
      return {
        sizeValue: resolveMapValue(sizeValueId, dictMap),
        partValue: resolveMapValue(partId, sizePartsMap),
        oldValue: defaultVal(value.oldValue, ''),
        newValue: defaultVal(value.newValue, '')
      };
    }
    case '100021':
      return {
        oldValue: defaultVal(pickDefined(getSchemeTypeLabel(value.oldValue), value.oldValue), ''),
        newValue: defaultVal(pickDefined(getSchemeTypeLabel(value.newValue), value.newValue), '')
      };
    case '100022':
      return {
        oldValue: formatPriceSchemeList(value.oldValue),
        newValue: formatPriceSchemeList(value.newValue)
      };
    case '100023':
      return {oldValue: defaultVal(value.oldValue, '')};
    case '100024':
      return {newValue: defaultVal(value.newValue, '')};
    default: {
      const goodsId = prefix0 && prefix0.type === 'search' ? prefix0.value : undefined;
      const result = {
        oldValue: defaultVal(value.oldValue, ''),
        newValue: defaultVal(value.newValue, '')
      };
      const searchPrefix = prefix.find((p) => p.type === 'search');
      if (searchPrefix) {
        result.attrName = resolveMapValue(searchPrefix.value, templateMap);
      }
      if (goodsId) {
        result.ymsSku = resolveMapValue(goodsId, productGoodsMap) || goodsId;
        result.sizeValue = resolveMapValue(goodsId, sizePartsMap);
      }
      if (prefix1 && prefix1.type === 'default') {
        result.countryName = resolveMapValue(prefix1.value, countryMap);
      }
      if (prefix1 && prefix1.type === 'search') {
        result.warehouseName = resolveMapValue(prefix1.value, warehouseMap);
        result.partValue = resolveMapValue(prefix1.value, sizePartsMap);
      }
      if (value.type === 'search') {
        const oldResolved = resolveDictArray(value.oldValue, dictMap);
        const newResolved = resolveDictArray(value.newValue, dictMap);
        result.oldValue = oldResolved || resolveMapValue(value.oldValue, warehouseMap);
        result.newValue = newResolved || resolveMapValue(value.newValue, warehouseMap);
      }
      return result;
    }
  }
};

/**
 * 解析 operateContent 并生成原值/新值对比项列表
 * @param {Object} parsed
 * @param {Object} axios
 * @param {string} [operationType]
 * @returns {Promise<Array>}
 */
const renderParsedContent = async (parsed, axios, operationType) => {
  const ids = collectIds(parsed);
  const [
    warehouseMap,
    attrMaps,
    sizePartsMap,
    productGoodsMap,
    countryMap,
    imageTypeMap
  ] = await Promise.all([
    fetchWarehouseMap(axios, ids.warehouseIds),
    fetchAttrMaps(axios, ids.attrTemplateIds),
    fetchSizePartsMap(axios, ids.sizePartsIds),
    fetchProductGoodsMap(axios, ids.productGoodsIds),
    fetchCountryMap(),
    fetchImageTypeMap()
  ]);
  const templateMap = attrMaps.templateMap;
  const dictMap = attrMaps.dictMap;

  await fetchAttrDictFallback(axios, ids.attrDictIds, dictMap);

  const maps = {warehouseMap, templateMap, dictMap, sizePartsMap, productGoodsMap, countryMap, imageTypeMap};
  const compareItems = [];
  const fieldKeys = getProductFieldKeys(parsed, operationType);

  fieldKeys.forEach((fieldKey) => {
    const items = parsed[fieldKey];
    if (!Array.isArray(items)) return;

    items.forEach((item) => {
      const val = buildTemplateVal(fieldKey, item, maps);
      const compareItem = getProductDetailsLogCompareItem(fieldKey, val);
      const hasCompareValue = compareItem && (
        compareItem.oldValue ||
        compareItem.newValue ||
        (compareItem.oldImages && compareItem.oldImages.length) ||
        (compareItem.newImages && compareItem.newImages.length)
      );
      if (hasCompareValue) {
        compareItems.push(compareItem);
      }
    });
  });

  return compareItems;
};

/**
 * 格式化商品详情操作日志
 * @param {string} operateContent - 原始 operateContent JSON 字符串
 * @param {Object} axios - axios 实例
 * @param {string} [operationType] - 操作类型，如 UPDATE_PRODUCT_INFO
 * @returns {Promise<{success: boolean, items: Array, raw?: string}>}
 */
export async function formatProductDetailsLog(operateContent, axios, operationType) {
  if (!operateContent) {
    return {success: false, items: [], raw: ''};
  }
  if (!isJSON(operateContent)) {
    return {success: false, items: [], raw: operateContent};
  }

  let parsed;
  try {
    parsed = JSON.parse(operateContent);
  } catch (e) {
    console.error('formatProductDetailsLog parse error:', e);
    return {success: false, items: [], raw: operateContent};
  }

  const isProductLog = isProductDetailsLogContent(parsed, operationType);
  if (operationType && operationType !== 'UPDATE_PRODUCT_INFO' && !isProductLog) {
    return {success: false, items: [], raw: operateContent};
  }
  if (!isProductLog) {
    return {success: false, items: [], raw: operateContent};
  }

  try {
    const items = await renderParsedContent(parsed, axios, operationType);
    return {
      success: true,
      items
    };
  } catch (e) {
    console.error('formatProductDetailsLog render error:', e);
    return {success: false, items: [], raw: operateContent};
  }
}
